import { useState, useEffect } from "react";
import { animated, easings, useSpring } from "@react-spring/web";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { pluralize } from "common/utils/format/word";

type AILoadingProps = {
  startTime: number | undefined;
  estimatedDuration: number;
  isDone?: boolean;
};

const STEPS = [
  "Thinking",
  "making your design print-ready",
  "sharpening your image clarity",
  "making your background removable",
  "enriching your colors",
  "smoothing your image edges",
  "running a final quality check"
];

function getRemainingTime(
  estimatedDuration: number,
  startTime: number,
  minRemainingTime: number = 0
) {
  const remaining = Math.max(0, estimatedDuration - (Date.now() - startTime));
  return Math.max(minRemainingTime, Math.ceil(remaining / 1000));
}

export function AILoading(props: AILoadingProps) {
  const { startTime = 0, estimatedDuration, isDone = false } = props;
  const { isMobile } = useResponsive();

  const elapsed = Math.max(0, Date.now() - startTime);
  const start = Math.min(1, elapsed / estimatedDuration); // 0-1, capped at 1

  const total = STEPS.length;

  const [currentIndex, setCurrentIndex] = useState(() =>
    Math.min(total - 1, Math.floor(start * total))
  );

  const [remainingTime, setRemainingTime] = useState(0);

  const currentStep = STEPS[currentIndex];
  const remainingTimeText = (() => {
    if (remainingTime <= 0) return "Almost done...";
    return isMobile
      ? `Time remaining: ${remainingTime}s`
      : `Estimated time remaining: ${pluralize(remainingTime, "second")}`;
  })();

  const { progress } = useSpring({
    from: { progress: start },
    to: { progress: 0.98 },
    config: {
      duration: estimatedDuration,
      mass: 2,
      tension: 300,
      friction: 15,
      easing: easings.easeOutQuad
    },
    onChange: ({ value }) => {
      const p = value["progress"];
      const idx = Math.min(total - 1, Math.floor(p * total));
      setCurrentIndex(idx);
      setRemainingTime(getRemainingTime(estimatedDuration, startTime));
    }
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setRemainingTime(getRemainingTime(estimatedDuration, startTime));
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime, estimatedDuration]);

  return (
    <div
      className="text-center text-sm font-semibold leading-tight text-gray-900"
      data-testid="ai-loading-container"
    >
      {isDone ? (
        <>
          <span className="text-purple-500">All done!</span> It&apos;s print perfect now.
        </>
      ) : (
        <>
          {currentIndex ? (
            <>
              <span className="text-purple-500">I&apos;m</span> {currentStep}
            </>
          ) : (
            currentStep
          )}
          ...
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
            <animated.div
              data-testid="ai-loading-progress-bar"
              className="h-full rounded-full bg-blue-600"
              style={{
                width: progress.to(p => `${Math.min(100, p * 100)}%`)
              }}
            />
          </div>
          <div className="mt-2 text-center text-xs font-normal text-gray-500 md:text-left">
            {remainingTimeText}
          </div>
        </>
      )}
    </div>
  );
}
